import { Fragment, useMemo } from 'react';
import styled from '@emotion/styled';
import {PrimaryOutlineButton} from 'shared/buttons';
import {NoRatesFound} from 'shared/components';
import {ConversieButtons} from 'shared/conversie';
import useMedia from 'shared/hooks/useMedia';
import analytics from 'shared/utility/analytics';
import {UPDATE} from 'shared/wizard/actions';
import TableHeader from './TableHeader';
import TableRows from './TableRows';
import {getParamsFromState} from '../../../../shared/helpers';
import {whiteListUrlParams} from '../../config';
import {RESULTS_LIMIT} from '../../constants';

const Divider = styled.hr`
  margin: 20px 0;
  border: none;
  
  @media ${({theme}) => theme.mobile} {
    margin: 24px 0;
    border-bottom: 1px dashed ${({theme}) => theme.borderColor};
  }
`;

function hasNoProducts(products, selectedFrts) {
    if (!products || products.length === 0) {
        return true;
    }

    return !products.some(product => {
        return product.frts.some(frt => {
            if (selectedFrts?.includes(frt.frt)) {
                return frt.interest_rate !== null;
            }

            return false;
        })
    })
}

const Table = props => {
    const {products, overview, preferredResults, sortedResults, input, output, dispatch, settings, app} = props;
    const {onMobile} = useMedia();
    const resultLength = (preferredResults || []).length + (sortedResults || []).length;
    const noProducts = useMemo(() => hasNoProducts(products, input.frt), [products, input.frt]);
    const preferredResultsLength = preferredResults.length;
    const sortByPreference = input.prefer_collaboration || input.prefer_auto_risk_class_reduction;
    const mortgageTypeValue = settings.meta.mortgage_types.find(type => type.value === input.mortgage_type).label;

    let noRatesMessage = undefined;
    if (noProducts) {
        // Alter message if mortgage type is 10(bridgingloan), as the noProducts probably is due to nhg filter or frt selection
        if (input.mortgage_type === 10 && (
            input.nhg ||
            !input.frt.some(frt => frt === 12 || frt === 24) // Should be true when 12 months and 24 months are not in the array
        )) {
            noRatesMessage = (
                <>
                    U zoekt naar overbruggingsrentes. Probeer de NHG tariefklasse te veranderen naar een andere
                    tariefklasse en
                    probeer de rentevasteperiode&apos;s voor 1 en 2 jaar vast te selecteeren.
                </>
            )
        }
    }

    return (
      <Fragment>
          <TableHeader {...props} />

          {noProducts && <NoRatesFound message={noRatesMessage}/>}

          {sortByPreference && (
              <>
                  {preferredResultsLength > 0 ? (
          <TableRows data={preferredResults} {...props} isPreferred type="S" />
        ) : (
          <NoRatesFound message="Er zijn geen producten gevonden om bovenaan te tonen" />
        )}
        <Divider />
      </>
    )}

          {!noProducts && sortedResults && sortedResults.length > 0 && (
              <TableRows
                  data={sortedResults}
                  type={sortByPreference ? 'R' : 'S'}
                  remainder={preferredResultsLength > RESULTS_LIMIT ? RESULTS_LIMIT : preferredResultsLength}
                  {...props}
              />
          )}

    {!noProducts && overview && resultLength > RESULTS_LIMIT && (
      <>
        <PrimaryOutlineButton
          css={{ margin: '16px 0', height: '56px' }}
          onClick={() => {
            analytics.send('Openen', 'Meer resultaten');
            dispatch({
              type: UPDATE,
              payload: { input: { ...input, showAllResults: !input.showAllResults } },
            });
          }}
        >
          {input.showAllResults ? 'Toon minder producten' : `Toon alle ${resultLength} producten`}
        </PrimaryOutlineButton>
        {onMobile && <Divider />}
      </>
    )}
    {onMobile && (
      <ConversieButtons
        settings={settings}
        app={app}
        onClick={() => {
          dispatch({
            type: UPDATE,
            payload: { input: { ...input, label_id: null } },
          });
        }}
        urlLeadFormParams={getParamsFromState({ ...input, ...output, mortgageTypeValue }, whiteListUrlParams)}
      />
    )}
  </Fragment>
    );
};

export default Table;
